import { useHandler } from "@redotech/react-util/hook";
import { AutomationForgeElementType } from "@redotech/redo-content-builder-common/forge/marketing-automation-forge-types";
import { PostPurchaseSegmentStep } from "@redotech/redo-flows-common/advanced-flow-db-parser";
import { Translate, useTranslation } from "@redotech/redo-i18n";
import GitBranchIcon from "@redotech/redo-icons/git-branch-01.svg";
import SwitchHorizontalIcon from "@redotech/redo-icons/switch-horizontal-01_filled.svg";
import { RedoCommandMenuItem } from "@redotech/redo-web/arbiter-components/command-menu/redo-command-menu";
import { RedoFeaturedIcon } from "@redotech/redo-web/arbiter-components/featured-icon/redo-featured-icon";
import { isDef } from "@redotech/util/checks";
import { Handle, Position } from "@xyflow/react";
import { memo, useMemo } from "react";
import { FlowEdge, NodeTypes, TypedFlowNode } from "../flow-converter";
import { PartialWithRequiredType } from "../steps";
import { BaseNode } from "./base-node";
import * as css from "./node.module.css";

export interface PostPurchaseSegmentNodeData {
  step: PartialWithRequiredType<PostPurchaseSegmentStep>;
  deleteElements: (id: string) => void;
  updateStep: (
    id: string,
    updatedStep: PartialWithRequiredType<PostPurchaseSegmentStep>,
  ) => void;
  readonly: boolean;
}

export const PostPurchaseSegmentNode = memo(function PostPurchaseSegmentNode({
  id,
  data,
  selected,
}: {
  id: string;
  data: PostPurchaseSegmentNodeData;
  selected?: boolean | undefined;
}) {
  const { t } = useTranslation();

  const handleSwapEdges = useHandler(() => {
    const swappedStep = {
      ...data.step,
      matchingNextId: data.step.nonMatchingNextId,
      nonMatchingNextId: data.step.matchingNextId,
    };
    data.updateStep(id, swappedStep);
  });

  const additionalMenuItems: RedoCommandMenuItem[] = useMemo(() => {
    if (data.readonly) {
      return [];
    }

    return [
      {
        text: t("Swap edges"),
        onClick: handleSwapEdges,
        Icon: SwitchHorizontalIcon,
        theme: "normal" as const,
      },
    ];
  }, [data.readonly, handleSwapEdges, t]);

  return (
    <BaseNode
      additionalMenuItems={additionalMenuItems}
      containerClassName={css.checkoutNode}
      icon={<RedoFeaturedIcon color="success" Icon={GitBranchIcon} size="sm" />}
      onDelete={data.readonly ? undefined : () => data.deleteElements(id)}
      selected={selected}
      step={data.step}
      title={t("Segment")}
    >
      <Handle id="top" position={Position.Top} type="target" />
      <Handle id="bottom" position={Position.Bottom} type="source" />
    </BaseNode>
  );
});

export function getPostPurchaseSegmentEdgesAndDropZones({
  stepId,
  matchingNextId,
  nonMatchingNextId,
  elementDropped,
  readonly,
  t,
}: {
  stepId: string;
  matchingNextId: string | undefined;
  nonMatchingNextId: string | undefined;
  elementDropped: (
    elementType: AutomationForgeElementType,
    idFrom: string,
    idTo?: string,
    conditionSide?: boolean,
  ) => void;
  readonly: boolean;
  t: Translate;
}): { edges: FlowEdge[]; dropZoneNodes: TypedFlowNode[] } {
  const edges: FlowEdge[] = [];
  const dropZoneNodes: TypedFlowNode[] = [];

  // Handle matching path
  if (isDef(matchingNextId)) {
    edges.push({
      id: stepId + "_matching",
      type: "labeled",
      source: stepId,
      sourceHandle: "bottom",
      target: matchingNextId,
      data: {
        label: t("Matches"),
        color: "gray",
        ...(!readonly ? { elementDropped } : {}),
        idFrom: stepId,
        idTo: matchingNextId,
        conditionSide: true,
      },
    });
  } else if (!readonly) {
    const dropZoneId = stepId + "_matching";
    dropZoneNodes.push({
      id: dropZoneId,
      type: NodeTypes.DROP_ZONE,
      position: { x: 0, y: 0 },
      data: {
        idFrom: stepId,
        idTo: undefined,
        conditionSide: true,
        elementDropped,
      },
    });
    edges.push({
      id: stepId + "_matching_edge",
      type: "labeled",
      source: stepId,
      sourceHandle: "bottom",
      target: dropZoneId,
      data: { label: t("Matches"), color: "gray" },
    });
  }

  // Handle non-matching path
  if (isDef(nonMatchingNextId)) {
    edges.push({
      id: stepId + "_non_matching",
      type: "labeled",
      source: stepId,
      sourceHandle: "bottom",
      target: nonMatchingNextId,
      data: {
        label: t("Does not match"),
        color: "gray",
        idFrom: stepId,
        idTo: nonMatchingNextId,
        ...(!readonly ? { elementDropped } : {}),
        conditionSide: false,
      },
    });
  } else if (!readonly) {
    const dropZoneId = stepId + "_non_matching";
    dropZoneNodes.push({
      id: dropZoneId,
      type: NodeTypes.DROP_ZONE,
      position: { x: 0, y: 0 },
      data: {
        idFrom: stepId,
        idTo: undefined,
        conditionSide: false,
        elementDropped,
      },
    });
    edges.push({
      id: stepId + "_non_matching_edge",
      type: "labeled",
      source: stepId,
      sourceHandle: "bottom",
      target: dropZoneId,
      data: { label: t("Does not match"), color: "gray" },
    });
  }

  return { edges, dropZoneNodes };
}
